/**
 * Reading filter state out of the query string.
 *
 * Filters live in the URL, so a filtered view can be linked to and survives a
 * reload. Each route's `validateSearch` reads its own keys with these, and
 * anything unrecognised -- a hand-edited link, a value from an older version
 * -- falls back to the page's default rather than failing. A default is never
 * written back: `undefined` is dropped from the URL, so an unfiltered page has
 * a clean address.
 *
 * Changes are written with `replace` and without resetting the scroll (see
 * `searchUpdate`): a filter is a view of the same page, not a step through
 * history, and Back should leave the page rather than undo the last click.
 */

/** One of a fixed set of values, or absent. */
export function oneOf<T extends string>(value: unknown, values: readonly T[]): T | undefined {
  return typeof value === "string" && (values as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

/**
 * Free text, or absent when empty. The router parses `?q=2026` as a number,
 * so numbers are taken back as the text they were typed as.
 */
export function text(value: unknown): string | undefined {
  if (typeof value === "number") {
    return String(value);
  }
  return typeof value === "string" && value !== "" ? value : undefined;
}

/** A calendar day, "YYYY-MM-DD". */
export function dayParam(value: unknown): string | undefined {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;
}

/** A month, "YYYY-MM". */
export function monthParam(value: unknown): string | undefined {
  return typeof value === "string" && /^\d{4}-\d{2}$/.test(value) ? value : undefined;
}

/** An on/off switch, written as `?key=1`. */
export function flag(value: unknown): true | undefined {
  return value === 1 || value === true || value === "1" ? true : undefined;
}

/**
 * Several values, written comma-separated (`?tags=phd,committee`) rather
 * than as the router's JSON array, which reads as noise in an address.
 */
export function list(value: unknown): string[] | undefined {
  const raw = typeof value === "string" ? value : typeof value === "number" ? String(value) : "";
  const items = raw.split(",").map((item) => item.trim()).filter((item) => item !== "");
  return items.length > 0 ? items : undefined;
}

/** The other direction: undefined for an empty list, so the key disappears. */
export function joinList(items: readonly string[]): string | undefined {
  return items.length > 0 ? items.join(",") : undefined;
}

/** How every filter change navigates: same page, no history entry, no jump. */
export const searchUpdate = { replace: true, resetScroll: false } as const;
